// Preview and test send, only on an explicit click. The service renders the e-mail with the tenant's
// branding, so it stays in a sandboxed iframe: never dangerouslySetInnerHTML.
import React, { useState } from "react";
import { useSelector } from "react-redux";
import { Alert, Button, Card, CircularProgress } from "@mui/material";
import { usePreviewReportMutation, useTestSendReportMutation } from "services/scheduledReports";
import { classifyReportError } from "utils/reportErrors";
import { testSendMessage } from "utils/reportDefinition";

export default function PreviewPane({ draftBody, onFieldErrors }) {
  const email = useSelector((state) => state.metaData.userInfo?.email);
  const [preview, previewResult] = usePreviewReportMutation();
  const [testSend, testResult] = useTestSendReportMutation();
  const [shown, setShown] = useState(null);
  const [message, setMessage] = useState(null);
  const busy = previewResult.isLoading || testResult.isLoading;

  const run = async (action, onDone) => {
    setMessage(null);
    const body = draftBody();
    if (!body) {
      setMessage({ severity: "warning", text: "Please correct the marked fields first." });
      return;
    }
    const res = await action({ token: window.localStorage.getItem("token"), body });
    if (res.error) {
      const problem = classifyReportError(res.error, { preview: true });
      if (problem.fieldErrors) onFieldErrors(problem.fieldErrors);
      const other = (problem.fieldErrors && problem.fieldErrors._other) || [];
      setMessage({ severity: "error", text: [problem.message, ...other].join(" ") });
      return;
    }
    onDone((res.data && res.data.payload) || {});
  };

  const warnings = (shown && shown.warnings) || [];

  return (
    <Card style={{ padding: "16px", position: "sticky", top: "84px" }}>
      <p style={{ fontSize: "15px", fontWeight: "bold", color: "#333333", margin: "0 0 12px 0", textTransform: "uppercase" }}>
        Preview
      </p>
      <div style={{ display: "flex", gap: "8px", alignItems: "center", marginBottom: "4px" }}>
        <Button variant="outlined" color="secondary" disabled={busy} onClick={() => run(preview, setShown)}>
          Preview
        </Button>
        <Button
          color="secondary"
          disabled={busy}
          onClick={() => run(testSend, (answer) => setMessage(testSendMessage(answer, email)))}
        >
          Send test to me
        </Button>
        {busy ? <CircularProgress size={20} color="secondary" /> : null}
      </div>
      <p style={{ fontSize: "12px", color: "grey", margin: "0 0 12px 0" }}>
        Uses the window of the last scheduled send. The test goes only to {email || "your own address"}.
      </p>
      {message ? (
        <Alert severity={message.severity} style={{ marginBottom: "12px" }}>
          {message.text}
        </Alert>
      ) : null}
      {shown ? (
        <>
          <p style={{ fontSize: "14px", margin: "0 0 4px 0" }}>
            <b>{shown.subject}</b>
          </p>
          <p style={{ fontSize: "12px", color: "grey", margin: "0 0 8px 0" }}>{shown.period && shown.period.label}</p>
          {warnings.map((line) => (
            <p key={line} style={{ fontSize: "12px", color: "#b26a00", margin: "0 0 8px 0" }}>
              {line}
            </p>
          ))}
          <iframe
            title="Report preview"
            sandbox=""
            srcDoc={shown.html}
            style={{ width: "100%", height: "560px", border: "1px solid #e0e0e0", opacity: busy ? 0.5 : 1 }}
          />
          <p style={{ fontSize: "12px", color: "grey", margin: "4px 0 0 0" }}>Preview of the report every recipient gets.</p>
        </>
      ) : (
        <p style={{ fontSize: "14px", color: "#c7c7c7", margin: 0 }}>Nothing to show yet. Choose Preview to see the e-mail.</p>
      )}
    </Card>
  );
}
